<template>
  <templates-contentful-content
    v-if="post"
    v-bind="propsData"
    template="ArticleWithSidebarV2"
    llm-type="Media"
    :data-testid="dataTestid"
  >
    <template #main-content>
      <RichTextRenderer
        :document="post.mainContent.json"
        :node-renderers="renderNodes()"
        :mark-renderers="renderMarks()"
      />

      <hr class="my-5 border-b border-b-gray-400" />
    </template>

    <template #sidebar-right>
      <V2MoleculesPostTileGroup
        :items="latestNews"
        title-class="mb-6"
        title="Latest news"
        type="sidebar"
        layout="small-tile"
        has-divider
        align-center
      />
    </template>

    <template #footer>
      <div class="bg-gray-10 px-4 xl:px-0 pt-6 pb-[2.625rem] md:pt-10 md:pb-12">
        <div class="w-full max-w-wrapper-lg mx-auto">
          <V2AtomsHighlight
            class="font-semibold !text-semantic-sizing-headline-sm mb-8"
            size="base"
          >
            About the author
          </V2AtomsHighlight>

          <V2OrganismAuthorWidget
            class="max-w-wrapper-sm mb-8"
            :author-name="post.author?.name"
            :author-image="post.author?.image"
            :author-url="post.author?.url"
            :job-title="post.author?.jobTitle"
            :social-media="post.author?.socialMedia"
          >
            <p>
              {{ post.author?.profileShort ?? post.author?.profile }}
            </p>
          </V2OrganismAuthorWidget>

          <hr class="mb-8 border-b border-b-semantic-outline-variant" />

          <template v-if="!_isEmpty(authorPosts)">
            <V2MoleculesPostTileGroup
              :items="authorPosts?.items"
              :items-per-row="3"
              :prefix-author-url="DEFAULT_AUTHOR_URL"
              image-lazy-load
            />
          </template>
        </div>
      </div>
    </template>
  </templates-contentful-content>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

import { storeToRefs } from 'pinia'

import RichTextRenderer from 'contentful-rich-text-vue-renderer'

import {
  DEFAULT_AUTHOR_URL
} from '~/constants/pages/contentful'

import {
  GET_FEATURE_IMAGE,
  RENDER_NODES,
  RENDER_MARKS
} from '~/helpers/contentful'

defineOptions({
  name: 'TemplatesContentfulMedia'
})

const props = defineProps({
  dataTestid: {
    type: String,
    default: 'contentful-media'
  },

  isPreview: {
    type: Boolean,
    default: false
  }
})

const $route = useRoute()
const _slug = $route.params?.slug

if (!_slug) {
  throw createError({
    statusCode: 404,
    statusMessage: 'POST DOES NOT EXIST',
    fatal: true
  })
}

const { devices } = deviceChecker()
const mediaStore = useMediaStore()
const {
  latestNews,
  authorPosts
} = storeToRefs(mediaStore)

const { data: postData } = await useAsyncData(`media-post-${_slug}`, () => mediaStore.getPost({
  slug: _slug,
  preview: props.isPreview
}))

const post = postData.value?.post

if (post?.redirectUrl?.slug) {
  await navigateTo(`${post?.redirectUrl?.slug}/`, {
    redirectCode: 301
  })
}

await useAsyncData(`media-author-posts-${_slug}`, async () => {
  await mediaStore.getAuthorPosts({
    limit: 3,
    authorUrl: post?.author?.url.replace(/\//g, ''),
    slugNot: $route.path
  })

  return true
})

const propsData = computed(() => {
  return {
    title: post?.title,
    lastUpdate: post.sys?.publishedAt ?? post.sys?.firstPublishedAt ?? post?.date,
    tags: post?.contentfulMetadata?.tags ?? [],
    featuredImage: GET_FEATURE_IMAGE(
      post.featureImage,
      'ArticleWithSidebarV2',
      devices.currBreakpoint.value
    ),
    hasShareButtons: true,
    postType: 'Media',
    author: post?.author,
    editor: post?.editor,
    breadcrumbs: [
      {
        text: 'Home',
        name: 'Home',
        url: '/'
      },
      {
        text: 'Media',
        name: 'Media',
        url: '/media/'
      },
      {
        text: post?.title,
        url: `${post.slug}/`,
        name: post?.title,
        active: true
      }
    ]
  }
})

const assetsMap = (() => {
  const map = new Map()

  if (_isEmpty(post)) {
    return map
  }

  const links = post.mainContent?.links

  for (const assets of links.assets.block) {
    map.set(assets.sys.id, assets)
  }

  return map
})()

const entriesMap = (() => {
  const map = new Map()

  if (_isEmpty(post)) {
    return map
  }

  const links = post.mainContent?.links

  for (const entry of links.entries.block) {
    map.set(entry.sys.id, entry)
  }

  return map
})()

const renderNodes = () => RENDER_NODES({
  entriesMap,
  assetsMap,
  template: 'ArticleWithSidebarV2',
  author: post?.author,
  inlineComponents: {
    CTA: resolveComponent('AtomsCta'),
    MoleculesCalculatorWithDisclaimer: resolveComponent('MoleculesCalculatorWithDisclaimer'),
    KeyPoints: resolveComponent('MoleculesKeyPoints'),
    InlineAge: resolveComponent('MoleculesInlineAge'),
    InlineCardCta: resolveComponent('V2MoleculesInlineCardCta'),
    InlineLifeStage: resolveComponent('MoleculesInlineLifeStage'),
    InlinePartner: resolveComponent('MoleculesInlinePartner'),
    VerticalFormWidget: resolveComponent('V2MoleculesVerticalFormWidgetSidebar'),

    MoleculesInlineLifeStage: resolveComponent('MoleculesInlineLifeStage'),
    Lifestage: resolveComponent('OrganismsInlineLifeStageAbTest'),
    State: resolveComponent('V2MoleculesInlineCardCta'),
    InlineAgeAbTest: resolveComponent('OrganismsInlineAgeAbTest'),
    AgeRange: resolveComponent('V2MoleculesInlineCardCta'),
    Partner: resolveComponent('MoleculesInlinePartner'),

    AUTHORBLOCK: resolveComponent('AtomsAuthorBlock'),
    FaqItem: resolveComponent('AtomsAccordion'),
    VideoAsset: resolveComponent('AtomsVideoFrame')
  }
})

const renderMarks = () => RENDER_MARKS

const noCanonicalLink = !!post?.noCanonicalLink
const noIndex = !!post?.noIndex
const canonicalLink = post?.canonicalLink || `${CC_DOMAIN}${post?.slug}/`

const PAGE_DETAILS = {
  title: removeHtmlString(post?.title) || 'Compare Club',
  ogTitle: post?.seoTitle ?? removeHtmlString(post?.title) ?? 'Compare Club',
  description: post?.metaDescription ?? '',
  ogUrl: canonicalLink,
  ogImage: post?.featureImage?.url || FEATURED_IMAGE_DEFAULT_IMAGE,
  ogType: 'product'
}

generateMeta({
  title: PAGE_DETAILS.title,
  ogTitle: PAGE_DETAILS.ogTitle,

  description: PAGE_DETAILS.description,
  ogDescription: PAGE_DETAILS.description,

  canonical: noCanonicalLink
    ? ''
    : PAGE_DETAILS.ogUrl,
  ogUrl: PAGE_DETAILS.ogUrl,
  ogImage: PAGE_DETAILS.ogImage,
  ogType: PAGE_DETAILS.ogType,

  noIndex,
  noFollow: noIndex
})

useJsonld(() => {
  const _path = trailingSlash($route.path)
  const routePage = 'media'
  const routeSlug = $route.params?.slug

  const datePublished = post?.date
  const dateModified = post?.date ?? post.sys?.publishedAt

  const authorName = post?.author?.name

  return {
    '@context': 'https://schema.org',
    '@graph': [
      GENERATE_LOGO_SCHEMA({
        articleUrl: `${trailingSlash(`${CC_DOMAIN}${_path}`)}#logo`,
        imageUrl: post?.featureImage?.url || FEATURED_IMAGE_DEFAULT_IMAGE,
        caption: post?.featureImage?.description || 'Compare Club'
      }),
      GENERATE_WEBPAGE_SCHEMA({
        page: _path,
        pageTitle: PAGE_DETAILS?.ogTitle ?? PAGE_DETAILS.title,
        pageDescription: PAGE_DETAILS.description,
        aboutPath: `/${routePage}/#service`,
        mainEntityPath: `${_path}#article`,
        primaryImageOfPagePath: `${_path}#primaryimage`,
        breadcrumbPath: `${_path}#breadcrumb`,
        datePublished,
        dateModified
      }),
      GENERATE_ARTICLE_SCHEMA({
        page: routePage,
        slug: routeSlug,
        author: authorName,
        headline: PAGE_DETAILS.title,
        audienceName: post?.audienceName,
        description: PAGE_DETAILS.description,
        datePublished,
        dateModified
      }),
      GENERATE_PERSON_SCHEMA({
        page: `${DEFAULT_AUTHOR_URL}${post?.author?.url}`,
        author: authorName
      })
    ]
  }
})
</script>
